import { useEffect, useRef } from 'react';

import { useT } from '../../i18n';
import { useAppStore } from '../../store/useAppStore';
import { useAufsichtStore } from '../../store/useAufsichtStore';
import { sollPausieren, starteInaktivitaetsuhr, type Inaktivitaetsuhr } from './inaktivitaet';
import { SchlafKnopf } from './SchlafKnopf';
import { SCHLAFECKE, zeigtSchwebendenKnopf } from './schlafecke';
import styles from './Schlafscheibe.module.css';

/**
 * Der Schlafknopf und die Scheibe, die nach fünf Minuten Ruhe darüber liegt.
 *
 * **Wozu.** Ein iPad, das während einer Prüfung minutenlang unbeachtet auf dem Tisch liegt,
 * zeigt die Bewertung eines Prüflings für jeden im Raum lesbar an — und brennt sie nebenbei
 * in den Bildschirm. Die Scheibe nimmt beides weg, ohne die App zu verlassen: der Bogen
 * bleibt stehen, wo er stand, nichts wird abgegeben, nichts gesichert, nichts verworfen.
 *
 * **Der Knopf gehört nicht in die Kopfzeile.** Die Uhr läuft in jeder Phase, die Kopfzeile
 * gibt es nur im Bogen — im Startablauf, in der Auswahl und über offenen Blättern nicht. Ein
 * fester Platz unten rechts ist der einzige, den es überall gibt, und der Pfeil im Schlaf
 * zeigt damit immer auf dieselbe Stelle.
 *
 * **`imFuss` verschiebt ihn trotzdem.** Wo es eine Fusszeile gibt — also im Bogen, solange
 * kein Blatt offen ist —, sitzt der Knopf dort neben „Absenden" (`bogen/Bogen.tsx`), und die
 * Ecke hier bleibt leer. Über einem offenen Blatt schwebt er wieder, denn die Fusszeile liegt
 * darunter und nimmt keine Berührung mehr an.
 *
 * ⚠️ **Die Scheibe schluckt jede Berührung** (`touch-action: none`, und sie liegt über
 * allem). Das ist der Sinn der Sache: ein Handballen auf dem liegenden iPad darf kein
 * Häkchen setzen. Nur der Knopf weckt.
 *
 * ⚠️ **Sie hat nichts mit Apples Bildschirmsperre zu tun.** Das iPad sperrt weiter nach
 * seiner eigenen Einstellung; diese Scheibe ist nur ein Vorhang innerhalb der App.
 */
export function Schlafscheibe({ imFuss = false }: { imFuss?: boolean }) {
  const t = useT();
  const schlaeft = useAppStore((s) => s.schlaeft);
  const medienAufnahme = useAppStore((s) => s.medienAufnahme);
  const vollbildEditor = useAppStore((s) => s.vollbildEditor);
  const shotStamp = useAppStore((s) => s.shotStamp);
  const mitteilungOffen = useAufsichtStore((s) => s.offen.length > 0);
  const uhr = useRef<Inaktivitaetsuhr | null>(null);

  useEffect(() => {
    const gestellt = starteInaktivitaetsuhr(() => {
      useAppStore.getState().setSchlaeft(true);
      // ⚠️ Mit dem Einschlafen fällt auch die Sperre vor den Einstellungen wieder zu. Ein
      // iPad, das entsperrt auf dem Tisch liegen bleibt, ist genau der Fall, den sie
      // verhindern soll (s. `sheets/einstellungenSperre.ts`).
      useAppStore.getState().setEinstellungenFrei(false);
    });
    uhr.current = gestellt;

    // ⚠️ **Abfangphase und `passive`.** Die Uhr will nur *wissen*, dass etwas geschah — sie
    // darf kein Ereignis verschlucken und keines aufhalten. In der Abfangphase erfährt sie
    // es auch dann, wenn ein Bauteil darunter die Weitergabe stoppt.
    const tippen = (): void => gestellt.tippen();
    const arten = ['pointerdown', 'keydown', 'wheel'] as const;
    for (const art of arten) window.addEventListener(art, tippen, { capture: true, passive: true });

    return () => {
      for (const art of arten) window.removeEventListener(art, tippen, { capture: true });
      gestellt.raeumeAuf();
      uhr.current = null;
    };
  }, []);

  // Anhalten, solange etwas läuft, das keine Berührung erzeugt — und solange die Scheibe
  // schon liegt. Der Aufräumschritt setzt fort: damit sind Anhalten und Fortsetzen paarig,
  // auch wenn zwei Gründe einander überlappen (`pausiere` zählt mit).
  const pausiert = sollPausieren({ schlaeft, medienAufnahme, shotStamp, mitteilungOffen });
  useEffect(() => {
    const gestellt = uhr.current;
    if (!gestellt || !pausiert) return;
    gestellt.pausiere();
    return () => gestellt.setzeFort();
  }, [pausiert]);

  return (
    <>
      {schlaeft && (
        <div
          className={styles.scheibe}
          // Kein `onClick`: ein Tipper daneben soll nichts tun, auch nicht aufwecken. Der
          // Knopf ist der Weg zurück, und nur er.
          aria-hidden="true"
        />
      )}
      {/* Das Merkmal sagt den Blättern, dass ein Tipper hier **kein** Klick nach draussen ist
          (s. `schlafecke.ts`). */}
      <div className={styles.ecke} data-schlaeft={schlaeft} {...{ [SCHLAFECKE]: '' }}>
        {schlaeft && (
          <div className={styles.hinweis}>
            <span className={styles.hinweisText}>{t.sleep.wakeHint}</span>
            {/*
              Der Pfeil vom Hinweis zum Knopf. Ein gebogener Strich, keine Grafik aus einer
              Datei: er muss zur Schriftgröße passen und in jedem Farbschema weiss bleiben —
              er liegt ja immer auf der dunklen Scheibe, nie auf dem hellen Bogen.
            */}
            <svg className={styles.pfeil} viewBox="0 0 76 40" fill="none" aria-hidden="true">
              <path
                d="M6 8C34 6 56 14 70 34"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              />
              {/*
                ⚠️ **Die Spitze muss aus der Endtangente der Kurve gerechnet sein**, sonst wird
                aus dem Pfeil ein Haken. Der letzte Griffpunkt der Kurve ist (56,14), ihr Ende
                (70,34) — die Richtung dort ist also (14,20) ≈ 55° abwärts. Die beiden Schenkel
                stehen je 30° davon weg (Länge 14) und landen darum auf (57.3,28.1) und
                (68.8,20.1).

                ⚠️ **30°, nicht weniger.** Bei einem flacheren Winkel läuft der obere Schenkel
                in die Kurve hinein und verschmilzt mit ihr: die Kurve führt bei x≈57 durch
                y≈20, der Schenkel endet dort 7,6 px tiefer — knapp genug bei 3 px Strichbreite.
              */}
              <path
                d="M57.3 28.1L70 34 68.8 20.1"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
        {/* ⚠️ **Im Schlaf immer**, auch wenn der Knopf sonst in der Fusszeile sitzt oder ein
            Vollbild-Editor offen ist: die Scheibe liegt über allem, ein Knopf darunter wäre
            sichtbar und tot. Die Regel steht in `schlafecke.ts`. */}
        {zeigtSchwebendenKnopf({ imFuss, vollbildEditor, schlaeft }) && (
          <SchlafKnopf gestalt="schwebend" />
        )}
      </div>
    </>
  );
}
